import { ReactNode } from 'react';
import { useField } from 'formik';
import {
  Box,
  Checkbox,
  FormGroup,
  InputLabel,
  FormControl,
  FormHelperText,
  FormControlLabel,
} from '@mui/material';

import { controlError } from 'utils';
import { QuestionLabel } from 'components/question-label';

export interface CheckboxFieldProps {
  name: string;
  label?: string;
  required?: boolean;
  children?: ReactNode;
  // Provide displayValue when mapValue stores anything other than string[].
  displayValue?: string[];
  mapValue?: (values: string[]) => unknown;
  options: { label: string; value: string }[];
}

export const CheckboxField = ({
  name,
  label,
  options,
  required,
  children,
  mapValue,
  displayValue,
}: CheckboxFieldProps) => {
  const [field, meta, helpers] = useField<unknown>(name);
  const selectedValues =
    displayValue ?? (Array.isArray(field.value) ? field.value : []);
  const errorText = controlError(meta, name, label);

  return (
    <Box>
      {label && (
        <InputLabel
          component="div"
          className="mb-2"
          required={required}
          sx={{ whiteSpace: 'pre-line' }}
        >
          <QuestionLabel value={label} />
        </InputLabel>
      )}
      <FormControl fullWidth required={required} error={Boolean(errorText)}>
        <FormGroup>
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              label={option.label}
              control={
                <Checkbox
                  name={name}
                  onBlur={() => helpers.setTouched(true, true)}
                  checked={selectedValues.includes(option.value)}
                  onChange={(event) => {
                    const nextValue = event.target.checked
                      ? [...selectedValues, option.value]
                      : selectedValues.filter(
                          (value) => value !== option.value
                        );
                    helpers.setValue(
                      mapValue ? mapValue(nextValue) : nextValue,
                      true
                    );
                  }}
                />
              }
            />
          ))}
        </FormGroup>

        {errorText && <FormHelperText>{errorText}</FormHelperText>}
      </FormControl>
      {children}
    </Box>
  );
};
